import type { BeforeSendFn } from 'posthog-js';

/**
 * PostHog `before_send` scrubber — AUTH-3 (2026-07-05).
 *
 * The redirect sign-in flow lands on /sign-in/callback with the FULL token
 * payload in the query string: access_token, refresh_token, expires_in, and
 * `user` (URL-encoded JSON including the user's email and ids). posthog-js
 * stamps `$current_url` (plus $referrer, $session_entry_url, and the
 * first-touch person props like initial_landing_url) onto outgoing events,
 * so every redirect sign-in wrote live credentials + PII into stored events,
 * decodable by anyone with PostHog read access.
 *
 * The callback page strips the query via history.replaceState as soon as it
 * mounts, but events captured BEFORE that (the boot $pageview, early
 * autocapture, the person $set_once batch) still see the raw URL. This
 * scrubber is the backstop: it redacts the sensitive param VALUES from every
 * string property on every outgoing event, including nested `$set` /
 * `$set_once` payloads.
 *
 * Redaction keeps the param key and replaces only the value
 * (`access_token=REDACTED`) rather than deleting the pair, and rewrites
 * nothing else in the string — untouched URLs stay byte-identical — so the
 * attribution/funnel tiles that match on URL shape keep working.
 *
 * CRE-983 hardening: the walk ALSO redacts free-text credential / PII shapes
 * (JWT, `Bearer <token>`, email addresses) that appear OUTSIDE query-param
 * form — e.g. a token or email echoed inside an exception value that
 * `enrichExceptionMessage` promotes to a top-level `error_message`, or nested
 * in `$exception_list[i].value`. These are high-precision so legitimate
 * ids/slugs/URLs are not touched. This is best-effort defense-in-depth, NOT a
 * guarantee against all free-form PII — arbitrary user text cannot be detected.
 *
 * Best-effort by house rule (CRE-641): scrubbing must never throw into the
 * capture path or drop an event.
 */

/**
 * Query params whose values must never reach PostHog. Mirror of
 * TOKEN_QUERY_PARAMS in the callback page (id_token added here for
 * forward-compat with provider-minted callbacks).
 */
const SENSITIVE_QUERY_PARAMS = [
  'access_token',
  'refresh_token',
  'id_token',
  'expires_in',
  'user',
  'session_id',
  // CRE-983: the magic-link verify URL is /auth/magic-link/verify?token=<JWT> —
  // a credential that was NOT covered before. `token`/`jwt` are credential-only
  // param names (no benign use), so redacting them everywhere a URL reaches
  // PostHog ($current_url, $referrer, an error string quoting the URL) is safe.
  // NOTE: `code` is deliberately NOT added — skim's OAuth callback lands with
  // access_token/refresh_token (not an authz code, see above), and `?code=` is a
  // common benign param (promo/country/error codes), so redacting it would
  // corrupt funnel tiles for no real gain.
  'token',
  'jwt',
  // The address itself, URL-encoded (`%40`), so the free-text email pattern
  // below never sees it. No page reads `?email=` any more, but a crafted link
  // still lands it in `$current_url`.
  'email',
] as const;

/**
 * Matches `?param=value`, `&param=value`, or `#param=value` for each
 * sensitive param, capturing the delimiter+key prefix so only the VALUE is
 * replaced. `[^&#]*` stops at the next param / fragment boundary, so
 * neighbouring params survive untouched.
 */
const SENSITIVE_PARAM_PATTERN = new RegExp(
  `([?&#](?:${SENSITIVE_QUERY_PARAMS.join('|')})=)[^&#]*`,
  'gi',
);

// ── Free-text credential / PII shapes (CRE-983 hardening) ──────────────────
// The param pass above only catches `?key=value` URL form. A token or email
// echoed as FREE TEXT inside a message — e.g. an exception value promoted to a
// top-level `error_message` (enrichExceptionMessage), or nested in
// `$exception_list[i].value` — is not param-shaped, so it would otherwise reach
// PostHog un-redacted, violating the CYA "no token/PII in event props" rule.
// These are deliberately HIGH-PRECISION (near-zero false positives) so they do
// not scrub legitimate ids/slugs/URLs the attribution & funnel tiles match on:
//   - JWT: three base64url segments led by the `eyJ` ({"...) header.
//   - Bearer: an Authorization-style `Bearer <token>`.
//   - Email: a local-part@domain.tld address.
// Best-effort defense-in-depth, NOT a guarantee against all free-form PII —
// arbitrary user text in an error string cannot be regex-detected.
const JWT_PATTERN = /eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g;
const BEARER_PATTERN = /\bBearer\s+[A-Za-z0-9._~+/=-]+/gi;
const EMAIL_PATTERN = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;

/**
 * Redact sensitive values inside an arbitrary string: sensitive URL query
 * params (value-only), plus free-text JWT / Bearer tokens and email addresses.
 * Returns the input byte-identical when nothing matches (cheap `includes`
 * gates keep the common clean string on the fast path).
 */
export const scrubAuthParamsFromString = (value: string): string => {
  let out = value;
  // 1) URL-shaped sensitive params (keeps the key, replaces the value).
  if (out.includes('=')) {
    out = out.replace(SENSITIVE_PARAM_PATTERN, '$1REDACTED');
  }
  // 2) Free-text credential / PII shapes — gated so a clean string does no work.
  if (out.includes('eyJ')) out = out.replace(JWT_PATTERN, 'JWT_REDACTED');
  if (/bearer/i.test(out)) out = out.replace(BEARER_PATTERN, 'Bearer REDACTED');
  if (out.includes('@')) out = out.replace(EMAIL_PATTERN, 'EMAIL_REDACTED');
  return out;
};

/** Cycle/pathology guard for the recursive property walk. */
const MAX_SCRUB_DEPTH = 6;

const scrubRecord = (props: Record<string, unknown>, depth: number): void => {
  if (depth > MAX_SCRUB_DEPTH) return;
  for (const key of Object.keys(props)) {
    const value = props[key];
    if (typeof value === 'string') {
      const scrubbed = scrubAuthParamsFromString(value);
      if (scrubbed !== value) props[key] = scrubbed;
    } else if (Array.isArray(value)) {
      for (let i = 0; i < value.length; i += 1) {
        const item: unknown = value[i];
        if (typeof item === 'string') {
          const scrubbed = scrubAuthParamsFromString(item);
          if (scrubbed !== item) value[i] = scrubbed;
        } else if (item && typeof item === 'object') {
          scrubRecord(item as Record<string, unknown>, depth + 1);
        }
      }
    } else if (value && typeof value === 'object') {
      scrubRecord(value as Record<string, unknown>, depth + 1);
    }
  }
};

/**
 * The `before_send` hook wired into `posthog.init` in
 * instrumentation-client.ts. Mutates the event in place (the documented
 * posthog-js pattern) and always returns it — a scrub failure must never
 * drop an event.
 */
export const scrubAuthParamsBeforeSend: BeforeSendFn = (event) => {
  if (!event) return event;
  try {
    if (event.properties) scrubRecord(event.properties, 0);
    if (event.$set) scrubRecord(event.$set, 0);
    if (event.$set_once) scrubRecord(event.$set_once, 0);
  } catch {
    // Best-effort — see doc comment.
  }
  return event;
};
